import React, { useState, useContext } from 'react';
import {
  Table, Badge, Button, Dropdown,
  Modal, Form, Alert, InputGroup, Row, Col,
} from 'react-bootstrap';
import AppContext from '../../../context/AppContext';

const fmt     = (n) => `PKR ${(n || 0).toLocaleString()}`;
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' })
  : 'N/A';

const PAYMENT_STATUS = {
  paid: 'success', unpaid: 'danger', pending: 'warning',
  refunded: 'info', failed: 'dark',
};

const SBadge = ({ status, map }) => {
  const bg = (map || PAYMENT_STATUS)[status?.toLowerCase()] || 'secondary';
  return (
    <Badge bg={bg} className="adm-bkd-badge">
      {status?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
    </Badge>
  );
};

const PaymentDetailModal = ({ payment, show, onHide, onViewBooking }) => {
  if (!payment) return null;
  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="adm-bkd-modal">
      <Modal.Header closeButton className="adm-bkd-modal-hd">
        <Modal.Title>
          <i className="bi bi-credit-card" />
          Payment Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="adm-bkd-modal-body">

        <div className="adm-bkd-modal-summary mb-3">
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Payment ID</span>
            <strong className="adm-bkd-mono">{payment.id}</strong>
          </div>
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Type</span>
            <Badge bg="primary" className="adm-bkd-badge">
              {payment.type?.replace(/_/g, ' ').toUpperCase()}
            </Badge>
          </div>
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Status</span>
            <SBadge status={payment.status} map={PAYMENT_STATUS} />
          </div>
        </div>

        <div className="adm-bkd-modal-section">
          <h6 className="adm-bkd-section-title">
            <i className="bi bi-receipt me-2" />Transaction Info
          </h6>
          <Row className="g-2">
            <Col md={4}>
              <span className="adm-bkd-lbl">Booking ID</span>
              <Button
                variant="link" className="adm-bkd-link-btn p-0 d-block"
                onClick={() => { onHide(); onViewBooking?.(payment.bookingId); }}
              >
                {payment.bookingId} <i className="bi bi-box-arrow-up-right ms-1" />
              </Button>
            </Col>
            <Col md={4}>
              <span className="adm-bkd-lbl">Transaction ID</span>
              <p className="adm-bkd-val adm-bkd-mono">{payment.transactionId || '—'}</p>
            </Col>
            <Col md={4}>
              <span className="adm-bkd-lbl">Method</span>
              <p className="adm-bkd-val">
                {payment.paymentMethod?.replace(/_/g, ' ').toUpperCase() || '—'}
              </p>
            </Col>
            <Col md={4}>
              <span className="adm-bkd-lbl">Date</span>
              <p className="adm-bkd-val">{fmtDate(payment.date)}</p>
            </Col>
            <Col md={4}>
              <span className="adm-bkd-lbl">Customer</span>
              <p className="adm-bkd-val">{payment.customerName || '—'}</p>
            </Col>
            <Col md={4}>
              <span className="adm-bkd-lbl">Vendor</span>
              <p className="adm-bkd-val">{payment.vendor?.name}</p>
            </Col>
          </Row>
        </div>

        <div className="adm-bkd-modal-section">
          <h6 className="adm-bkd-section-title">
            <i className="bi bi-cash-stack me-2" />Breakdown
          </h6>
          <div className="adm-bkd-breakdown">
            <div className="adm-bkd-breakdown-row">
              <span>Total Amount</span>
              <strong className="text-success">{fmt(payment.amount)}</strong>
            </div>
            {payment.commission > 0 && (
              <div className="adm-bkd-breakdown-row">
                <span>Commission (15%)</span>
                <strong className="text-primary">{fmt(payment.commission)}</strong>
              </div>
            )}
            {payment.vendorPayout > 0 && (
              <div className="adm-bkd-breakdown-row adm-bkd-breakdown-total">
                <span>Vendor Payout</span>
                <strong className="text-info">{fmt(payment.vendorPayout)}</strong>
              </div>
            )}
          </div>
        </div>

      </Modal.Body>
      <Modal.Footer className="adm-bkd-modal-footer">
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
        <Button
          className="adm-bkd-btn-primary"
          onClick={() => { onHide(); onViewBooking?.(payment.bookingId); }}
        >
          <i className="bi bi-calendar-check me-2" />View Booking
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
const RefundModal = ({ booking, show, onHide, onProcess }) => {
  const [type,   setType]   = useState('full');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error,  setError]  = useState('');
  const [busy,   setBusy]   = useState(false);

  if (!booking) return null;
  const max = booking.totalAmount || 0;

  const handleType = (t) => {
    setType(t);
    setAmount(t === 'full' ? String(max) : '');
    setError('');
  };

  const handleSubmit = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Enter a valid amount'); return; }
    if (amt > max)        { setError(`Max refundable: ${fmt(max)}`); return; }
    if (!reason.trim())   { setError('Reason is required'); return; }
    setBusy(true);
    Promise.resolve(onProcess?.(booking.id, amt, reason))
      .then(() => {
        setBusy(false);
        setType('full'); setAmount(''); setReason(''); setError('');
        onHide();
      })
      .catch(() => { setBusy(false); setError('Failed. Try again.'); });
  };

  return (
    <Modal show={show} onHide={onHide} centered className="adm-bkd-modal">
      <Modal.Header closeButton className="adm-bkd-modal-hd adm-bkd-modal-hd-danger">
        <Modal.Title>
          <i className="bi bi-arrow-counterclockwise" />
          Process Refund
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="adm-bkd-modal-body">
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <div className="adm-bkd-modal-summary mb-3">
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Booking ID</span>
            <strong className="adm-bkd-mono">{booking.id}</strong>
          </div>
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Customer</span>
            <strong>{booking.customerName}</strong>
          </div>
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Max Refund</span>
            <strong className="text-success">{fmt(max)}</strong>
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">Refund Type</Form.Label>
          <div className="adm-bkd-radio-group">
            <Form.Check type="radio" id="adm-bkd-rp-full"
              label={`Full Refund (${fmt(max)})`}
              checked={type === 'full'}
              onChange={() => handleType('full')} />
            <Form.Check type="radio" id="adm-bkd-rp-partial"
              label="Partial Refund"
              checked={type === 'partial'}
              onChange={() => handleType('partial')} />
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">
            Refund Amount <span className="text-danger">*</span>
          </Form.Label>
          <InputGroup>
            <InputGroup.Text className="adm-bkd-filter-icon">PKR</InputGroup.Text>
            <Form.Control
              type="number" className="adm-bkd-filter-input"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={type === 'full'} min="0" max={max}
            />
          </InputGroup>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">
            Reason <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="textarea" rows={3} className="adm-bkd-filter-input"
            placeholder="Reason for refund…"
            value={reason} onChange={e => setReason(e.target.value)}
          />
        </Form.Group>

        <Alert variant="warning" className="py-2 mb-0">
          <i className="bi bi-exclamation-triangle me-2" />
          This action cannot be undone. Customer will be notified.
        </Alert>
      </Modal.Body>
      <Modal.Footer className="adm-bkd-modal-footer">
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
        <Button variant="danger" onClick={handleSubmit} disabled={busy || !amount || !reason}>
          {busy
            ? <><span className="spinner-border spinner-border-sm me-2" />Processing…</>
            : <><i className="bi bi-check-circle me-2" />Process Refund</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
  const SettlementModal = ({ payout, show, onHide, onApprove }) => {
  const [method, setMethod] = useState('bank_transfer');
  const [txnId,  setTxnId]  = useState('');
  const [notes,  setNotes]  = useState('');
  const [error,  setError]  = useState('');
  const [busy,   setBusy]   = useState(false);

  if (!payout) return null;

  const handleApprove = () => {
    if (!txnId.trim()) { setError('Transaction / Reference ID is required'); return; }
    setBusy(true);
    Promise.resolve(onApprove?.(payout.id, method, txnId, notes))
      .then(() => {
        setBusy(false);
        setMethod('bank_transfer'); setTxnId(''); setNotes(''); setError('');
        onHide();
      })
      .catch(() => { setBusy(false); setError('Failed. Try again.'); });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="adm-bkd-modal">
      <Modal.Header closeButton className="adm-bkd-modal-hd adm-bkd-modal-hd-success">
        <Modal.Title>
          <i className="bi bi-check-circle" />
          Approve Vendor Settlement
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="adm-bkd-modal-body">
        {error && <Alert variant="danger" className="py-2 mb-3">{error}</Alert>}

        <div className="adm-bkd-modal-summary mb-3">
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Payout ID</span>
            <strong className="adm-bkd-mono">{payout.id}</strong>
          </div>
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Vendor</span>
            <strong>{payout.vendor?.name}</strong>
          </div>
          <div className="adm-bkd-modal-summary-item">
            <span className="adm-bkd-lbl">Booking</span>
            <strong className="adm-bkd-mono">{payout.bookingId}</strong>
          </div>
        </div>

        <div className="adm-bkd-breakdown mb-3">
          <div className="adm-bkd-breakdown-row">
            <span>Booking Total</span>
            <strong>{fmt(payout.amount + (payout.commission || 0))}</strong>
          </div>
          <div className="adm-bkd-breakdown-row">
            <span>Platform Commission (15%)</span>
            <strong className="text-danger">— {fmt(payout.commission || 0)}</strong>
          </div>
          <div className="adm-bkd-breakdown-row adm-bkd-breakdown-total">
            <span>Vendor Payout</span>
            <strong className="text-success">{fmt(payout.amount)}</strong>
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">
            Payment Method <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            className="adm-bkd-filter-select" value={method}
            onChange={e => setMethod(e.target.value)}
          >
            <option value="bank_transfer">Bank Transfer</option>
            <option value="jazzcash">JazzCash</option>
            <option value="easypaisa">EasyPaisa</option>
            <option value="cheque">Cheque</option>
          </Form.Select>
          <Form.Text className="text-muted">
            Bank transaction ID, mobile wallet reference, or cheque number
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">
            Transaction / Reference ID <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text" className="adm-bkd-filter-input"
            placeholder="Bank TXN ID, wallet ref, or cheque number"
            value={txnId}
            onChange={e => { setTxnId(e.target.value); setError(''); }}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="adm-bkd-filter-label">Additional Notes (Optional)</Form.Label>
          <Form.Control
            as="textarea" rows={2} className="adm-bkd-filter-input"
            placeholder="Any notes about this settlement…"
            value={notes} onChange={e => setNotes(e.target.value)}
          />
        </Form.Group>

        <Alert variant="info" className="py-2 mb-2">
          <i className="bi bi-info-circle me-2" />
          Confirm that <strong>{fmt(payout.amount)}</strong> has been transferred via{' '}
          <strong>{method.replace('_', ' ').toUpperCase()}</strong>.
        </Alert>
        <Alert variant="warning" className="py-2 mb-0">
          <i className="bi bi-exclamation-triangle me-2" />
          This action cannot be reversed once approved.
        </Alert>
      </Modal.Body>
      <Modal.Footer className="adm-bkd-modal-footer">
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
        <Button variant="success" onClick={handleApprove} disabled={busy || !txnId.trim()}>
          {busy
            ? <><span className="spinner-border spinner-border-sm me-2" />Processing…</>
            : <><i className="bi bi-check-circle-fill me-2" />Approve Settlement</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const AdmHotelPaymentTable = ({ payments = [], bookings = [], onViewBooking, onRefundProcess }) => {
  const { setSelectedPaymentId } = useContext(AppContext);
  const [selPayment, setSelPayment] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [refundBk,   setRefundBk]   = useState(null);
  const [showRefund, setShowRefund] = useState(false);

  const userPayments = payments.filter(p => p.type === 'user_payment');

  const handleView = (p) => {
    setSelPayment(p);
    setSelectedPaymentId(p.id);
    setShowDetail(true);
  };

  const handleRefund = (p) => {
    setRefundBk(bookings.find(b => b.id === p.bookingId) || null);
    setShowRefund(true);
  };

  return (
    <>
      <div className="adm-bkd-table-card">
        <div className="adm-bkd-table-hd">
          <span className="adm-bkd-table-hd-title">
            <i className="bi bi-cash-stack" /> User Payment Records
          </span>
          <span className="adm-bkd-table-count">{userPayments.length} records</span>
        </div>
        <div className="table-responsive">
          <Table className="adm-bkd-table">
            <thead>
              <tr>
                <th>Payment ID</th>
                <th>Booking ID</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Vendor</th>
                <th>Amount</th>
                <th>Commission</th>
                <th>Vendor Payout</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userPayments.length === 0 ? (
                <tr>
                  <td colSpan="10" className="adm-bkd-empty-state">
                    <i className="bi bi-inbox" /><p>No payment records found</p>
                  </td>
                </tr>
              ) : userPayments.map(p => (
                <tr key={p.id}>
                  <td>
                    <span className="adm-bkd-mono adm-bkd-id">{p.id}</span>
                    {p.transactionId && (
                      <small className="d-block text-muted">{p.transactionId}</small>
                    )}
                  </td>
                  <td>
                    <Button
                      variant="link" size="sm"
                      className="adm-bkd-link-btn p-0"
                      onClick={() => onViewBooking?.(p.bookingId)}
                    >
                      {p.bookingId} <i className="bi bi-box-arrow-up-right ms-1" />
                    </Button>
                  </td>
                  <td>{fmtDate(p.date)}</td>
                  <td className="fw-semibold">{p.customerName}</td>
                  <td className="fw-semibold">{p.vendor?.name}</td>
                  <td><strong className="text-success">{fmt(p.amount)}</strong></td>
                  <td><span className="text-primary fw-semibold">{fmt(p.commission)}</span></td>
                  <td>
                    <span className="text-info fw-semibold">{fmt(p.vendorPayout)}</span>
                    {p.payoutStatus && (
                      <Badge
                        bg={p.payoutStatus === 'pending' ? 'warning' : 'success'}
                        className="d-block mt-1 adm-bkd-badge"
                        style={{ width: 'fit-content' }}
                      >
                        {p.payoutStatus === 'pending' ? 'Payout Pending' : 'Payout Done'}
                      </Badge>
                    )}
                  </td>
                  <td><SBadge status={p.status} map={PAYMENT_STATUS} /></td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-secondary" size="sm"
                        className="adm-bkd-action-toggle"
                      >
                        <i className="bi bi-three-dots-vertical" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="adm-bkd-dropdown-menu">
                        <Dropdown.Item onClick={() => handleView(p)}>
                          <i className="bi bi-eye me-2" />View Details
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => onViewBooking?.(p.bookingId)}>
                          <i className="bi bi-calendar-check me-2" />View Booking
                        </Dropdown.Item>
                        {p.status === 'paid' && (
                          <Dropdown.Item onClick={() => handleRefund(p)}>
                            <i className="bi bi-arrow-counterclockwise me-2" />Process Refund
                          </Dropdown.Item>
                        )}
                        <Dropdown.Divider />
                        <Dropdown.Item>
                          <i className="bi bi-download me-2" />Download Receipt
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <PaymentDetailModal
        payment={selPayment} show={showDetail}
        onHide={() => setShowDetail(false)}
        onViewBooking={onViewBooking}
      />
      <RefundModal
        booking={refundBk} show={showRefund}
        onHide={() => setShowRefund(false)}
        onProcess={onRefundProcess}
      />
    </>
  );
};

export const AdmVendorSettlementTable = ({ payments = [], onApprove }) => {
  const vendorPayouts = payments.filter(p => p.type === 'vendor_payout');
  const [selPayout, setSelPayout] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const totalPending   = vendorPayouts
    .filter(p => p.status === 'pending')
    .reduce((s, p) => s + p.amount, 0);
  const totalCompleted = vendorPayouts
    .filter(p => p.status === 'paid')
    .reduce((s, p) => s + p.amount, 0);

  return (
    <>
      <div className="adm-bkd-table-card">
        <div className="adm-bkd-table-hd">
          <span className="adm-bkd-table-hd-title">
            <i className="bi bi-building" /> Vendor Settlement Records
          </span>
          <span className="adm-bkd-table-count">{vendorPayouts.length} records</span>
        </div>
        <div className="table-responsive">
          <Table className="adm-bkd-table">
            <thead>
              <tr>
                <th>Payout ID</th>
                <th>Booking ID</th>
                <th>Vendor</th>
                <th>Payout Amount</th>
                <th>Due Date</th>
                <th>Method</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendorPayouts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="adm-bkd-empty-state">
                    <i className="bi bi-inbox" /><p>No vendor settlements found</p>
                  </td>
                </tr>
              ) : vendorPayouts.map(p => (
                <tr key={p.id}>
                  <td>
                    <span className="adm-bkd-mono adm-bkd-id">{p.id}</span>
                    {p.transactionId && (
                      <small className="d-block text-muted">Txn: {p.transactionId}</small>
                    )}
                  </td>
                  <td><span className="adm-bkd-mono text-primary">{p.bookingId}</span></td>
                  <td>
                    <div className="fw-semibold">{p.vendor?.name}</div>
                    <small className="text-muted">Hotel Vendor</small>
                  </td>
                  <td><strong className="text-success">{fmt(p.amount)}</strong></td>
                  <td>
                    {fmtDate(p.payoutDueDate || p.date)}
                    {p.payoutDueDate &&
                      new Date(p.payoutDueDate) < new Date() &&
                      p.status === 'pending' && (
                        <Badge
                          bg="danger"
                          className="d-block mt-1 adm-bkd-badge"
                          style={{ width: 'fit-content' }}
                        >
                          Overdue
                        </Badge>
                      )}
                  </td>
                  <td>
                    {p.paymentMethod
                      ? (
                        <Badge bg="info" className="adm-bkd-badge">
                          {p.paymentMethod.replace(/_/g, ' ').toUpperCase()}
                        </Badge>
                      )
                      : <span className="text-muted">Not Set</span>}
                  </td>
                  <td>
                    <Badge
                      bg={p.status === 'pending' ? 'warning' : p.status === 'paid' ? 'success' : 'danger'}
                      className="adm-bkd-badge"
                    >
                      {p.status === 'pending' ? 'Pending Approval'
                        : p.status === 'paid' ? 'Completed'
                        : p.status}
                    </Badge>
                  </td>
                  <td>
                    {p.status === 'pending' ? (
                      <Button
                        variant="success" size="sm"
                        className="adm-bkd-approve-btn"
                        onClick={() => { setSelPayout(p); setShowModal(true); }}
                      >
                        <i className="bi bi-check-circle me-1" />Approve
                      </Button>
                    ) : (
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="outline-secondary" size="sm"
                          className="adm-bkd-action-toggle"
                        >
                          <i className="bi bi-three-dots-vertical" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="adm-bkd-dropdown-menu">
                          <Dropdown.Item>
                            <i className="bi bi-eye me-2" />View Details
                          </Dropdown.Item>
                          <Dropdown.Item>
                            <i className="bi bi-download me-2" />Download Receipt
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="adm-bkd-table-footer">
          <div className="adm-bkd-summary-item">
            <span className="adm-bkd-summary-label">Total Pending</span>
            <span className="adm-bkd-summary-value text-warning">{fmt(totalPending)}</span>
          </div>
          <div className="adm-bkd-summary-item">
            <span className="adm-bkd-summary-label">Total Completed</span>
            <span className="adm-bkd-summary-value text-success">{fmt(totalCompleted)}</span>
          </div>
        </div>
      </div>

      <SettlementModal
        payout={selPayout} show={showModal}
        onHide={() => setShowModal(false)}
        onApprove={onApprove}
      />
    </>
  );
};